import Vue from "vue";
import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {PlayerDurum} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";
import {uyapEngelDurumu, uyapEngelKilidiniKaldir} from "@/plugins/uyap-plugin/UyapEngeli";
import {uyapEngeliMetniniSuz, yenidenDenemeOnayi} from "@/utils/uyapEngel";

/**
 * UYAP insan doğrulaması engeli: panel durumu (DEV-3572, SOZLESME §5.2, §4.6, §13.4).
 *
 * İKİ AYRI kayıt: `panel` = bu sekmenin kendi kilidi (plugin modül durumunun aynası; sayfa yenilenince
 * kalkar, kalıcı depoya YAZILMAZ), `imzaci` = botun engeli (panelin bildiği kadarıyla: YALNIZ imzacının canlı
 * 'notification.uyapEngeli' duyurusundan kurulur; açılışta sunucudan KURULMAZ, bkz. aşağıdaki not). Modül
 * namespaced DEĞİLDİR (imzaKimligi gibi). `@/main` import EDİLMEZ (dairesel import): HTTP için `Vue.prototype.$http`.
 *
 * Panel kapısı yalnız KENDİ kilidine bakar (plugin `uyapEngelKilidiAktifMi`); imzacı kaydı bilgi ve
 * Başlat onayı içindir (plugin'e dış kaynak olarak yalnız `autoPlayer`'ın 'oto' yayınını susturmak için verilir).
 *
 * Sunucudaki engel olayı (`GET /api/v1/uyap-engel`, `engel_acik`) botun gerçek durumunun KANITI DEĞİLDİR: olay son
 * 24 saat açık sayılır, imzacının yeniden açılışı onu kapatmaz ve eski imzacı penceresi olayı yine yazar. Bu yüzden
 * panel açılışta sunucudan imzacı aynası ('Bot' satırı) KURMAZ (bayat şerit, yanlış 'Duraklatıldı. UYAP engeli
 * sürüyor.' ipucu ve autoPlayer'ın sessizce bastırılması buradan geliyordu; arayuz-1, uyum-1). Sunucu kaydı YALNIZ
 * `playerBaslatOnayli` içinde, panelin bildiği bir kilit/duyuru yokken onay sorulup sorulmayacağına karar vermek için okunur.
 */

/** `GET` ve `DELETE` aynı yol (test kilitler; SOZLESME §7.5). */
export const UYAP_ENGEL_KALDIR_URL = "/api/v1/uyap-engel";
export const UYAP_ENGEL_DURUM_URL = UYAP_ENGEL_KALDIR_URL;
const KALDIRMA_BEKLEME_MS = 3000;
const DURUM_OKUMA_ZAMAN_ASIMI_MS = 2000;

/** DELETE sorgu dizgisindeki `neden` (`params:{neden}`; SOZLESME §7.4: yoksa/başkaysa 'yeniden_deneme'). */
export type UyapEngelKaldirmaNedeni = "yeniden_deneme" | "baslat_onayi";

export interface UyapEngeliKaydi {
    zaman: number;
    metin: string;
}

export interface UyapEngeliState {
    panel: UyapEngeliKaydi | null;
    imzaci: UyapEngeliKaydi | null;
}

/** {aktif:true, zaman?, metin?} -> kayıt; aktif:false / null / nesne olmayan -> null. UYAP metni güvenilmez veridir: süzülür. */
export function engelKaydiniOlustur(ham: any): UyapEngeliKaydi | null {
    if (!ham || typeof ham !== "object" || ham.aktif !== true) return null;
    const zaman = typeof ham.zaman === "number" && isFinite(ham.zaman) && ham.zaman > 0 ? ham.zaman : Date.now();
    return {zaman: zaman, metin: uyapEngeliMetniniSuz(ham.metin)};
}

/** `soz` bitince ya da `ms` dolunca (hangisi önceyse) çözülür; asla reddetmez. */
function sureliBekle(soz: Promise<any>, ms: number): Promise<void> {
    return new Promise<void>((coz) => {
        const zamanlayici = setTimeout(coz, ms);
        const bitir = () => {
            clearTimeout(zamanlayici);
            coz();
        };
        soz.then(bitir, bitir);
    });
}

/** `soz`un değeri; süre dolarsa ya da reddedilirse `null`. Asla reddetmez (fail-open okuma). */
function sureliOku(soz: Promise<any>, ms: number): Promise<any> {
    return new Promise<any>((coz) => {
        const zamanlayici = setTimeout(() => coz(null), ms);
        soz.then((deger) => {
            clearTimeout(zamanlayici);
            coz(deger);
        }, () => {
            clearTimeout(zamanlayici);
            coz(null);
        });
    });
}

/**
 * Sunucu bu büroda son 24 saatte açılmış, kaldırılmamış bir engel olayı biliyor mu (SOZLESME §13.4)?
 * Gölge mod dahil (fren açık/kapalı fark etmez). Hata, 2 sn zaman aşımı, eski backend (404) ve
 * `engel_acik` olmayan cevap: false = "bilinmiyor" (bugünkü onaysız davranış, fail-open).
 * YALNIZ `playerBaslatOnayli` çağırır (onay sorulsun mu kararı); durumu aynaya YAZMAZ.
 */
async function sunucudaAcikEngelVarMi(): Promise<boolean> {
    const http = Vue.prototype.$http;
    if (!http || typeof http.get !== "function") return false;
    let cevap: any = null;
    try {
        cevap = await sureliOku(Promise.resolve(http.get(UYAP_ENGEL_DURUM_URL, {
            sessiz: true,
            timeout: DURUM_OKUMA_ZAMAN_ASIMI_MS
        })), DURUM_OKUMA_ZAMAN_ASIMI_MS);
    } catch (e) {
        cevap = null;
    }
    return !!cevap && typeof cevap === "object" && cevap.engel_acik === true;
}

export function uyapEngeliModuluOlustur(): Module<UyapEngeliState, any> {
    /** Uçuştaki Başlat onayı: çift tık aynı sonucu paylaşır, ikinci pencere açılmaz. */
    let baslatUcusta: Promise<boolean> | null = null;

    const mutations: MutationTree<UyapEngeliState> = {
        /** payload: plugin kancasının olayı (`{aktif:true, metin, zaman}`) ya da null (kilit kalktı). */
        setUyapEngeliPanel(state, ham: any) {
            state.panel = engelKaydiniOlustur(ham);
        },
        /** payload: `imzaciEngelBildirimi(notification)` çıktısı (`{aktif:true, zaman}`) ya da null. */
        setUyapEngeliImzaci(state, ham: any) {
            state.imzaci = engelKaydiniOlustur(ham);
        },
        uyapEngeliImzaciTemizle(state) {
            state.imzaci = null;
        },
        /** Yalnız testler. */
        uyapEngeliSifirla(state) {
            state.panel = null;
            state.imzaci = null;
            baslatUcusta = null;
        }
    };

    const getters: GetterTree<UyapEngeliState, any> = {
        uyapEngeliPanel: (state) => state.panel,
        uyapEngeliImzaci: (state) => state.imzaci,
        uyapEngeliPanelAktif: (state) => state.panel !== null,
        uyapEngeliImzaciAktif: (state) => state.imzaci !== null,
        uyapEngeliAktif: (state) => state.panel !== null || state.imzaci !== null
    };

    const actions: ActionTree<UyapEngeliState, any> = {
        /**
         * Plugin kilidi ile aynayı eşitler: kanca kaydından önce kurulmuş kilidi alır; plugin kendi içinde
         * kaldırdıysa (beklenmez, savunma) aynayı temizler. App.vue kanca kaydından sonra ve 'player'
         * olayında çağırır.
         */
        uyapEngeliSenkronla({state, commit}) {
            const durum = uyapEngelDurumu();
            if (durum && state.panel === null) {
                commit("setUyapEngeliPanel", {aktif: true, zaman: durum.zaman, metin: durum.metin});
            } else if (!durum && state.panel !== null) {
                commit("setUyapEngeliPanel", null);
            }
        },
        /**
         * Kullanıcı eylemiyle kilidi kaldırır: plugin kilidi (`'panel'` kaynağı), panel aynası ve sunucu freni.
         * Sunucu isteği fail-open ve sessizdir (eski backend 404, Mongo hatası 503 verir; hepsi yutulur,
         * `{kaldirildi:false}` da hata sayılmaz); en çok 3 sn beklenir çünkü Başlat 'oto' yayınlamadan ÖNCE
         * fren kalkmış olmalı. `neden` SORGU DİZGİSİYLE gider (`params`): gövde ve `Content-Type` YOK,
         * yeni başlık YOK (SOZLESME C9).
         */
        async uyapEngeliniKaldir({commit}, secenek?: { neden?: UyapEngelKaldirmaNedeni, imzaciyiDaTemizle?: boolean }) {
            uyapEngelKilidiniKaldir("panel");
            commit("setUyapEngeliPanel", null);
            if (secenek && secenek.imzaciyiDaTemizle === true) commit("uyapEngeliImzaciTemizle");
            const http = Vue.prototype.$http;
            if (!http || typeof http.delete !== "function") return;
            const neden: UyapEngelKaldirmaNedeni = secenek && secenek.neden === "baslat_onayi" ? "baslat_onayi" : "yeniden_deneme";
            try {
                await sureliBekle(Promise.resolve(http.delete(UYAP_ENGEL_KALDIR_URL, {
                    params: {neden: neden},
                    sessiz: true
                })), KALDIRMA_BEKLEME_MS);
            } catch (e) {
                // fail-open: sunucu ucu yok/hatalı olsa da kullanıcının kaldırması geçerlidir
            }
        },
        /**
         * K5 + SOZLESME §13.4: panelde Başlat kilidi kaldırır, ama aynı onay penceresiyle. 17 Başlat noktasının
         * TEK girişi (DevamEdenGorevList + 16 işlem-detay görünümü).
         *  - Panelin bildiği bir kilit (panel ya da imzacı kaydı) varsa onay istenir.
         *  - Bilmiyorsa (panel yenilendi / engelden sonra açıldı) sunucuya sorulur: bu büroda kaldırılmamış engel
         *    olayı varsa (`engel_acik`) yine onay istenir (sunucu kaydı bu TEK yerde okunur; ayna kurulmaz).
         *    Okuma hatası/zaman aşımı (2 sn) = bugünkü onaysız davranış (fail-open).
         *  - Dene: kaldırma (DELETE <= 3 sn beklenir) TAMAMLANDIKTAN SONRA 'oto' yayınlanır. DELETE beklenirken
         *    yeni bir engel kilidi kurulduysa (panelin isteği ya da imzacının duyurusu) 'oto' YAYINLANMAZ: yeni
         *    kilit kullanıcıya kalır (imzacıdaki kilit-1 düzeltmesinin panel eşi).
         * Yayın çıplak SAYI 0'dır (`PlayerDurum.oto`): imzacının tür kuralında "kullanıcı eylemi" budur; metin "0"
         * otomatik/yeniden oynatmadır ve kilitliyken reddedilir. Bu yüzden `String(...)` ya da başka sarmalama YOK.
         * Dönüş: 'oto' yayınlandı mı (Vazgeç ya da yayın yok -> false).
         */
        playerBaslatOnayli({getters, dispatch}, socketClient: any): Promise<boolean> {
            if (baslatUcusta) return baslatUcusta;
            const is = (async (): Promise<boolean> => {
                let onayGerekli = getters.uyapEngeliAktif === true;
                if (!onayGerekli) onayGerekli = await sunucudaAcikEngelVarMi();
                if (onayGerekli) {
                    if (!(await yenidenDenemeOnayi())) return false;
                    await dispatch("uyapEngeliniKaldir", {neden: "baslat_onayi", imzaciyiDaTemizle: true});
                    if (uyapEngelDurumu() || getters.uyapEngeliAktif === true) return false;
                }
                if (!socketClient || typeof socketClient.emit !== "function") return false;
                socketClient.emit("player", PlayerDurum.oto);
                return true;
            })();
            baslatUcusta = is;
            const birak = () => {
                if (baslatUcusta === is) baslatUcusta = null;
            };
            is.then(birak, birak);
            return is;
        }
    };

    return {state: (): UyapEngeliState => ({panel: null, imzaci: null}), getters: getters, mutations: mutations, actions: actions};
}

export default uyapEngeliModuluOlustur();
